import * as React from 'react'
import {ReactElement} from 'react'
import {Nullable} from '../../../app/TypeHelpers'
import {StyledDiv, StyledLi, StyledUl} from '../../../styledComponents/StyledTypeDefinitions'
import styled from 'styled-components'
import {BreakPoint, Colors, DispWidthSize, FontSize} from '../../../styledComponents/Constants'
import {FlexGrowContent, FlexStaticContent, RowFlexContainer} from '../../../styledComponents/FlexComponents'
import {generateMedia} from 'styled-media-query'
import {Box} from '@mui/material'
import NatSessionRepo from '../session/repository/NatSessionRepo'
import {PlainAncerLink} from '../../../styledComponents/Links'
import {SessionStorageHelper} from '../../../SessionStorageHelper'
import {
    GuestLoginInfoSessionStorageKey,
    NatGuestLoginInfoSessionStorageHelper,
} from '../../common/authentication/NatGuestLoginInfoSessionStorageHelper'
import {convertToRoomNumberForResident, getQueryParameter} from '../../common/util/Natutil'
import {ROLE_FOR_GUEST} from '../../../CodeMaster'
import EventEmitterExGuest from '../../../components/shared/pageBaseComponents/EventEmitterExGuest'
import {GuestHeaderLogo, GuestManualButton} from './NatGuestHeaderAreaParts'
import {AsyncCommunicationProps, withAsyncCommunication} from '../../../components/shared/hoc/withAsyncCommunication'
import {NatOwnershipTransfersRepo} from '../ownershipTransfers/repository/NatOwnershipTransfersRepo'
import {Location, NavigateFunction} from 'react-router-dom'

interface Props {
    navigate: NavigateFunction
    location: Location
    ownershipTransfersRepo: NatOwnershipTransfersRepo
    sessionRepo: NatSessionRepo
    loginedFlg: boolean
}

interface States {
    manualUrl: Nullable<string>
    isLoadCompleted: boolean
}

interface ExternalProps {
    eventEmitter: EventEmitterExGuest
}

class NatGuestHeaderAreaComponent
    extends React.Component<Props & AsyncCommunicationProps & ExternalProps, States> {
    constructor(props: Props & AsyncCommunicationProps & ExternalProps) {
        super(props)
        this.state = {
            manualUrl: null,
            isLoadCompleted: false
        }

        this.logoClick = this.logoClick.bind(this)
        this.handleClickManualButton = this.handleClickManualButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const manualUrl = await this.getOwnershipTransferManualUrl()
            this.setState({manualUrl: manualUrl})
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    // ロゴ クリック
    async logoClick(): Promise<void> {
        if (this.props.loginedFlg) {
            const {ownershipTransfersRepo, eventEmitter} = this.props
            const condominiumCd = NatGuestLoginInfoSessionStorageHelper
                .getStringItemOrNull(GuestLoginInfoSessionStorageKey.SELECTED_CONDOMINIUM_CD) ?? ''

            await eventEmitter.showLoading()
            try {
                const accessKeyByLoginUser = await this.props.asyncCallFunction(
                    ownershipTransfersRepo.getAccessKeyByLoginUser.bind(ownershipTransfersRepo, condominiumCd),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    }
                )
                const accessKey = accessKeyByLoginUser?.details?.accessKey ?? ''
                this.props.navigate(`/guest/mypage?accessKey=${accessKey}`)
            } finally {
                eventEmitter.closeLoading()
            }
        }
    }

    // 物件情報
    renderCondominiumInfo(): Nullable<ReactElement> {
        if (!this.props.loginedFlg) {
            // 未ログインの場合は、非表示
            return null
        }
        return (
            <HeaderPcCondominium>
                <GuestCondominiumFlexPc>
                    <CondominiumIcon/>
                    <Box padding={`5px`}>
                        {SessionStorageHelper.getStringItemOrNull(
                            GuestLoginInfoSessionStorageKey.SELECTED_CONDOMINIUM_NAME,
                        )}
                    </Box>
                </GuestCondominiumFlexPc>
            </HeaderPcCondominium>
        )
    }

    // ユーザ情報
    renderUserInfo(): Nullable<ReactElement> {
        if (!this.props.loginedFlg) {
            // 未ログインの場合は、非表示
            return null
        }

        const ridgeName = NatGuestLoginInfoSessionStorageHelper.getStringItemOrNull(
            GuestLoginInfoSessionStorageKey.SELECTED_RIDGE_NAME) ?? ''
        const roomNumber = convertToRoomNumberForResident(NatGuestLoginInfoSessionStorageHelper.getStringItemOrNull(
            GuestLoginInfoSessionStorageKey.SELECTED_ROOM_NUMBER) ?? '')
        const displayRoomNumber = `${ridgeName} ${roomNumber}`
        // セッションストレージから取得したロールIDがゲストのもの以外の場合は空文字
        const roleName = (() => {
            try {
                return ROLE_FOR_GUEST.codeToEnumConstants(
                    NatGuestLoginInfoSessionStorageHelper.getStringItemOrNull(
                        GuestLoginInfoSessionStorageKey.LOGIN_USER_ROLE_ID) ?? '',
                ).label
            } catch {
                return ''
            }
        })()

        return (
            <>
                <UserInfoIcon/>
                <UserInfoText>
                    {displayRoomNumber}<br/>{roleName}様
                </UserInfoText>
            </>)
    }


    // 物件/ユーザ情報
    renderCondominiumAndUserInfo(): Nullable<ReactElement> {
        if (!this.props.loginedFlg) {
            return null
        }

        return (
            <FlexSpContent>
                <GuestFlexUserCondominiumSp>
                    <GuestFlexCondominiumItemSp>
                        <CondominiumIcon/>
                        <BuildingInfo>
                            {SessionStorageHelper.getStringItemOrNull(
                                GuestLoginInfoSessionStorageKey.SELECTED_CONDOMINIUM_NAME,
                            )}
                        </BuildingInfo>
                    </GuestFlexCondominiumItemSp>
                    <ResidentFlexUserItemSp>
                        {this.renderUserInfo()}
                    </ResidentFlexUserItemSp>
                </GuestFlexUserCondominiumSp>
            </FlexSpContent>
        )
    }

    handleClickManualButton = () => {
        const manualUrl = this.state.manualUrl ?? ''
        window.open(manualUrl, '_blank')
    }

    async getOwnershipTransferManualUrl(): Promise<Nullable<string>> {
        const {ownershipTransfersRepo, eventEmitter} = this.props
        const accessKey = getQueryParameter('accessKey', this.props.location.search)

        const response = await this.props.asyncCallFunction(
            ownershipTransfersRepo.getOwnershipTransferManualUrl.bind(ownershipTransfersRepo, accessKey),
            (httpErrorOrNetworkError) => {
                eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )
        return response ? response?.details?.manualUrl : null
    }

    renderPcHeader(): ReactElement {

        return (
            <GuestFlexHeaderBack>
                <StyledNavigationHeader>
                    <HeaderFlex>
                        <HeaderSiteLogo>
                            <FlexAncerLink
                                onClick={(event: React.MouseEvent) => {
                                    event.preventDefault()
                                    this.logoClick()
                                }}
                                style={{
                                    cursor: `${(this.props.loginedFlg) ? 'pointer' : 'default'}`,
                                }}>
                                <GuestHeaderLogo/>
                            </FlexAncerLink>
                        </HeaderSiteLogo>
                        {this.renderCondominiumInfo()}
                        <FlexGrowContent/>
                        <HeaderPcUserInfo>
                            <GuestCondominiumFlexPc>
                                {this.renderUserInfo()}
                            </GuestCondominiumFlexPc>
                        </HeaderPcUserInfo>
                        {this.props.loginedFlg &&
                            <GuestManualButton
                                onClick={this.handleClickManualButton}
                            />
                        }
                    </HeaderFlex>
                </StyledNavigationHeader>
            </GuestFlexHeaderBack>
        )
    }

    renderSpHeader(): ReactElement {

        return (
            <GuestFlexHeaderBack>
                <HeaderFlex>
                    <HeaderSiteLogo>
                        <FlexAncerLink
                            onClick={(event: React.MouseEvent) => {
                                event.preventDefault()
                                this.logoClick()
                            }}
                            style={{
                                cursor: `${(this.props.loginedFlg) ? 'pointer' : 'default'}`,
                            }}>
                            <GuestHeaderLogo/>
                        </FlexAncerLink>
                    </HeaderSiteLogo>
                    <FlexGrowContent/>
                    {this.props.loginedFlg &&
                        <GuestManualButton
                            onClick={this.handleClickManualButton}
                        />
                    }
                </HeaderFlex>
            </GuestFlexHeaderBack>
        )
    }

    render(): Nullable<ReactElement> {
        return (
            <>
                <FlexPcContent>
                    {this.renderPcHeader()}
                </FlexPcContent>
                <FlexSpContent>
                    {this.renderSpHeader()}
                </FlexSpContent>
                {this.renderCondominiumAndUserInfo()}
            </>
        )
    }
}

export default withAsyncCommunication(NatGuestHeaderAreaComponent)

// ブレイクポイント
const breakPoint: any = generateMedia({
    sp: `${BreakPoint.Sp}`,
})

// PC表示/SP非表示
const FlexPcContent: StyledDiv = styled.div`
  display: block;

  ${breakPoint.lessThan('sp')`
    display: none;
  `}
`
// PC非表示/SP表示
const FlexSpContent: StyledDiv = styled.div`
  display: none;

  ${breakPoint.lessThan('sp')`
    display: block;
  `}
`

// ヘッダーエリア（Back）
const GuestFlexHeaderBack = styled(FlexStaticContent)`
  background-color: ${Colors.GuestPrimary};
`

// ヘッダーエリア（Flex）
const HeaderFlex: StyledUl = styled.ul`
  display: flex;
  justify-content: space-between;
  list-style: none;
  margin: 0;
  padding: 0;
  box-sizing: inherit;
  color: ${Colors.Surface};
  width: 100%;
`

// ヘッダーエリア
const StyledNavigationHeader = styled(RowFlexContainer)`
  position: relative;
  max-width: ${DispWidthSize.MaxWidth};
  height: 100%;
  margin-left: auto;
  margin-right: auto;
  z-index: 10;
  box-shadow: 6px 0 5px -5px ${Colors.Shadow}, -6px 0 5px -5px ${Colors.Shadow};
  background-color: ${Colors.GuestPrimary};
  color: ${Colors.Surface};
  font-size: ${FontSize.Small};
`

// Flex Item(Logo)
const HeaderSiteLogo: StyledLi = styled.li`
  display: flex;
  align-content: center;
  text-align: center;
  position: relative;
  align-items: center;
  margin-left: 0;
  flex-grow: 0;
  padding: 0px 10px;
  min-height: 60px;
  box-sizing: border-box;

  ${breakPoint.lessThan('sp')`
     padding: 0;
     margin-left: 15px;
     flex-grow: 10;
  `}
`

// Flex Item(Condominium)
const HeaderPcCondominium = styled(HeaderSiteLogo)`
  padding: 0 15px 0 0;
  flex-basis: 300px;
`

// Flex Item(UserInfo)
const HeaderPcUserInfo = styled(HeaderSiteLogo)`
  padding: 0 175px 0 0;
  flex-basis: 145px;
`

const UserInfoText: StyledDiv = styled.div`
  text-align: left;
  width: 95px;
`

// 物件エリア（Flex）
const GuestCondominiumFlexPc: StyledDiv = styled.div`
  display: flex;
  align-items: center;
`

// ユーザ/物件情報エリア（back）
const GuestFlexUserCondominiumSp = styled(FlexStaticContent)`
  background-color: ${Colors.Surface};
  display: flex;
  align-items: center;
  font-size: ${FontSize.Small};
  padding: 5px 10px;
`

// Flex Item(Condominium) SP
const GuestFlexCondominiumItemSp: StyledDiv = styled.div`
  display: flex;
  align-items: center;
  width: 60%;
`

// 物件アイコン
const CondominiumIcon: StyledDiv = styled.div`
  padding: 15px;
  margin: 5px;
  background-image: url(/resources/images/icons/icon_building_white.png);
  background-position: left;
  background-repeat: no-repeat;
  background-size: 24px;

  ${breakPoint.lessThan('sp')`
     background-image: url(/resources/images/icons/icon_building.png);
  `}
`

// 物件情報
const BuildingInfo = styled(FlexGrowContent)`
  padding-left: 5px;
`

const FlexAncerLink = styled(PlainAncerLink)`
  min-height: 0;
  color: ${Colors.Surface}
`

// ユーザアイコン
const UserInfoIcon = styled(CondominiumIcon)`
  background-image: url(/resources/images/icons/icon_user_white.png);
  ${breakPoint.lessThan('sp')`
     background-image: url(/resources/images/icons/icon_user.png);
  `}
`

// Flex Item(User) SP
const ResidentFlexUserItemSp: StyledDiv = styled.div`
  display: flex;
  align-items: center;
  width: 40%;
`
